/**
 * ======================================================================================================================
 * 用語集ナビゲーション - GlossaryNavigation
 * ======================================================================================================================
 * 
 * アンカーリンクによるスムーススクロール機能を提供するWeb Component
 * 
 * 機能:
 * - 9個のカテゴリボタン（あ行～わ行）クリックで対象セクションへスクロール
 * - ヘッダー高さを考慮したオフセット調整
 * - スムーススクロールアニメーション
 * - アクセシビリティ対応（キーボード操作）
 * 
 * @version 1.0
 * @date 2026-01-28
 */

class GlossaryNavigation extends HTMLElement {
  constructor() {
    super();
    
    // 設定
    this.config = {
      headerOffset: 80, // ヘッダー高さ（調整可能）
      scrollDuration: 600, // スクロールアニメーション時間（ms）
      scrollBehavior: 'smooth' // スクロール動作
    };
    
    // 要素の初期化
    this.buttons = [];
    this.activeButton = null;
  }
  
  /**
   * カスタム要素がDOMに追加されたときに実行
   */
  connectedCallback() {
    this.init();
  }
  
  /**
   * 初期化処理
   */
  init() {
    // ナビゲーションボタンを取得
    this.buttons = Array.from(this.querySelectorAll('.c-glossary__nav-button'));
    
    if (this.buttons.length === 0) {
      console.warn('[GlossaryNavigation] ナビゲーションボタンが見つかりません');
      return;
    }
    
    // イベントリスナーを設定
    this.attachEventListeners();
    
    // 初期化完了ログ
    this.log('初期化完了', {
      buttonsCount: this.buttons.length
    });
  }
  
  /**
   * イベントリスナーを設定
   */
  attachEventListeners() {
    this.buttons.forEach((button, index) => {
      // クリックイベント
      button.addEventListener('click', this.handleClick.bind(this));
      
      // キーボードイベント（Enter / Space）
      button.addEventListener('keydown', this.handleKeydown.bind(this));
    });
  }
  
  /**
   * クリックイベントハンドラ
   * @param {Event} e - クリックイベント
   */
  handleClick(e) {
    e.preventDefault();
    
    const button = e.currentTarget;
    const targetId = button.getAttribute('href');
    
    if (!targetId) {
      console.warn('[GlossaryNavigation] href属性が見つかりません', button);
      return;
    }
    
    // スクロール実行
    this.scrollToTarget(targetId, button);
  }
  
  /**
   * キーボードイベントハンドラ
   * @param {KeyboardEvent} e - キーボードイベント
   */
  handleKeydown(e) {
    // Enter または Space キー
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      
      const button = e.currentTarget;
      const targetId = button.getAttribute('href');
      
      if (targetId) {
        this.scrollToTarget(targetId, button);
      }
    }
  }
  
  /**
   * 対象要素へスクロール
   * @param {string} targetId - 対象要素のID（例: "#category-a"）
   * @param {HTMLElement} button - クリックされたボタン要素
   */
  scrollToTarget(targetId, button) {
    try {
      // "#" を除去
      const cleanId = targetId.replace('#', '');
      const targetElement = document.getElementById(cleanId);
      
      if (!targetElement) {
        console.warn('[GlossaryNavigation] 対象要素が見つかりません', targetId);
        return;
      }
      
      // スクロール位置を計算
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - this.config.headerOffset;
      
      // スムーススクロール実行
      this.smoothScrollTo(offsetPosition);
      
      // アクティブ状態を更新
      this.updateActiveButton(button);
      
      // ログ出力
      this.log('スクロール実行', {
        targetId: targetId,
        offsetPosition: offsetPosition
      });
      
    } catch (error) {
      console.error('[GlossaryNavigation] スクロールエラー', error);
    }
  }
  
  /**
   * スムーススクロールを実行
   * @param {number} position - スクロール位置（Y座標）
   */
  smoothScrollTo(position) {
    // モダンブラウザ対応
    if ('scrollBehavior' in document.documentElement.style) {
      window.scrollTo({
        top: position,
        behavior: this.config.scrollBehavior
      });
    } else {
      // フォールバック（古いブラウザ）
      window.scrollTo(0, position);
    }
  }
  
  /**
   * アクティブボタン状態を更新
   * @param {HTMLElement} activeButton - アクティブにするボタン
   */
  updateActiveButton(activeButton) {
    // 既存のアクティブ状態をクリア
    this.buttons.forEach(button => {
      button.setAttribute('aria-current', 'false');
      button.classList.remove('c-glossary__nav-button--active');
    });
    
    // 新しいアクティブ状態を設定
    if (activeButton) {
      activeButton.setAttribute('aria-current', 'true');
      activeButton.classList.add('c-glossary__nav-button--active');
      this.activeButton = activeButton;
    }
  }
  
  /**
   * カスタム要素がDOMから削除されたときに実行
   */
  disconnectedCallback() {
    // イベントリスナーをクリーンアップ
    this.buttons.forEach(button => {
      button.removeEventListener('click', this.handleClick.bind(this));
      button.removeEventListener('keydown', this.handleKeydown.bind(this));
    });
    
    this.log('クリーンアップ完了');
  }
  
  /**
   * デバッグログ出力
   * @param {string} message - ログメッセージ
   * @param {Object} data - 追加データ
   */
  log(message, data = {}) {
    if (window.Shopify && window.Shopify.designMode) {
      console.log(`[GlossaryNavigation] ${message}`, data);
    }
  }
}

/**
 * ======================================================================================================================
 * カスタム要素の登録
 * ======================================================================================================================
 */

// カスタム要素を登録（重複登録を防ぐ）
if (!customElements.get('glossary-navigation')) {
  customElements.define('glossary-navigation', GlossaryNavigation);
}

/**
 * ======================================================================================================================
 * スクロール位置監視（オプション機能）
 * ======================================================================================================================
 * 
 * 現在表示中のカテゴリに応じてナビゲーションボタンのアクティブ状態を更新
 * パフォーマンスへの影響を考慮し、オプション機能として実装
 */

class GlossaryScrollObserver {
  constructor() {
    this.sections = [];
    this.buttons = [];
    this.currentSection = null;
    this.observer = null;
    
    // スロットリング用
    this.isScrolling = false;
    this.scrollTimeout = null;
  }
  
  /**
   * 初期化
   */
  init() {
    // セクションとボタンを取得
    this.sections = Array.from(document.querySelectorAll('.c-glossary__section'));
    this.buttons = Array.from(document.querySelectorAll('.c-glossary__nav-button'));
    
    if (this.sections.length === 0 || this.buttons.length === 0) {
      return;
    }
    
    // Intersection Observer が利用可能な場合
    if ('IntersectionObserver' in window) {
      this.setupIntersectionObserver();
    } else {
      // フォールバック: スクロールイベント
      this.setupScrollListener();
    }
  }
  
  /**
   * Intersection Observer をセットアップ
   */
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.updateActiveButtonBySection(entry.target);
        }
      });
    }, options);
    
    // 各セクションを監視
    this.sections.forEach(section => {
      this.observer.observe(section);
    });
  }
  
  /**
   * スクロールリスナーをセットアップ（フォールバック）
   */
  setupScrollListener() {
    window.addEventListener('scroll', () => {
      if (!this.isScrolling) {
        window.requestAnimationFrame(() => {
          this.checkVisibleSection();
          this.isScrolling = false;
        });
        
        this.isScrolling = true;
      }
    });
  }
  
  /**
   * 現在表示中のセクションをチェック
   */
  checkVisibleSection() {
    const scrollPosition = window.pageYOffset + window.innerHeight / 3;
    
    for (const section of this.sections) {
      const rect = section.getBoundingClientRect();
      const top = rect.top + window.pageYOffset;
      const bottom = top + rect.height;
      
      if (scrollPosition >= top && scrollPosition <= bottom) {
        this.updateActiveButtonBySection(section);
        break;
      }
    }
  }
  
  /**
   * セクションに対応するボタンをアクティブに
   * @param {HTMLElement} section - アクティブなセクション
   */
  updateActiveButtonBySection(section) {
    const sectionId = section.getAttribute('id');
    
    if (!sectionId || this.currentSection === sectionId) {
      return;
    }
    
    this.currentSection = sectionId;
    
    // 対応するボタンを探してアクティブに
    this.buttons.forEach(button => {
      const buttonTarget = button.getAttribute('href').replace('#', '');
      
      if (buttonTarget === sectionId) {
        button.setAttribute('aria-current', 'true');
        button.classList.add('c-glossary__nav-button--active');
      } else {
        button.setAttribute('aria-current', 'false');
        button.classList.remove('c-glossary__nav-button--active');
      }
    });
  }
  
  /**
   * クリーンアップ
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

/**
 * ======================================================================================================================
 * 初期化
 * ======================================================================================================================
 */

// DOMが読み込まれたら初期化（オプション機能）
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollObserver);
} else {
  initScrollObserver();
}

function initScrollObserver() {
  // スクロール監視機能を有効化したい場合はコメントアウトを外す
  // const scrollObserver = new GlossaryScrollObserver();
  // scrollObserver.init();
}

/**
 * ======================================================================================================================
 * エクスポート（モジュールとして使用する場合）
 * ======================================================================================================================
 */

// ES Modules として使用する場合
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    GlossaryNavigation,
    GlossaryScrollObserver
  };
}
